import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations, exampleTasks } from '../translations';
import {
  pixelChat,
  getPixelSession,
  renderVideoFromSolution,
  getVideoStatus,
  generateSimilarProblems,
  submitPracticeSolution,
  uploadImageBase64,
  getCreditStatus,
} from '../services/api';
import {
  ArrowUp, Camera, Paperclip, Video as VideoIcon, History,
  BookOpen, Sparkles, Loader, X, Gem, Plus, Lightbulb, ChevronRight,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import PracticeProblemModal from '../components/PracticeProblemModal';
import './ProblemSolver.css';

// ── Normalise LaTeX delimiters so remark-math can parse older content ────────
const normaliseLatex = (text) => {
  if (!text) return text;
  return text
    .replace(/\\\[/g, '$$').replace(/\\\]/g, '$$')   // \[...\] → $$...$$
    .replace(/\\\(/g, '$').replace(/\\\)/g, '$');      // \(...\) → $...$
};

// ── Markdown renderer with LaTeX ─────────────────────────────────────────────
const MD = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
    {normaliseLatex(children)}
  </ReactMarkdown>
);

// ── Pixel AI icon (reusable) ──────────────────────────────────────────────────
const PixelIcon = ({ size = 'md' }) => (
  <div className={`px-icon px-icon-${size}`}>
    <div className="px-eye" />
    <div className="px-eye" />
  </div>
);

// ── Single chat message ───────────────────────────────────────────────────────
const ChatMessage = ({
  msg, t,
  onCreateVideo, onGeneratePractice, onOpenPractice,
  isCreatingVideo, videoUrl, videoStatus,
  practiceReady, isGeneratingPractice, practiceCount,
  COST_RENDER_VIDEO,
}) => {
  if (msg.role === 'user') {
    return (
      <div className="msg-user-row">
        <div className="msg-user-bubble">
          {msg.imagePreview && (
            <img src={msg.imagePreview} alt="upload" className="msg-img" />
          )}
          {msg.content && <span>{msg.content}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="msg-ai-row">
      <PixelIcon size="sm" />
      <div className="msg-ai-body">
        <div className="msg-ai-text">
          <MD>{msg.content}</MD>
        </div>

        {msg.isFirst && (
          <div className="msg-actions">
            {videoUrl ? (
              <div className="msg-video">
                <video controls controlsList="nodownload" src={videoUrl} />
              </div>
            ) : isCreatingVideo ? (
              <div className="msg-action-btn msg-action-loading">
                <Loader size={15} className="spin" />
                <span>{t.creatingVideo || 'Creating video…'}{videoStatus ? ` · ${videoStatus}` : ''}</span>
              </div>
            ) : (
              <button onClick={onCreateVideo} className="msg-action-btn msg-action-video">
                <VideoIcon size={15} />
                <span>{t.createVideo || 'Create Video'}</span>
                <span className="msg-credit">-{COST_RENDER_VIDEO} <Gem size={11} /></span>
              </button>
            )}

            {practiceReady && practiceCount > 0 ? (
              <button onClick={onOpenPractice} className="msg-action-btn msg-action-success">
                <BookOpen size={15} />
                <span>{t.practiceReady || 'Practice Ready!'} ({practiceCount})</span>
              </button>
            ) : isGeneratingPractice ? (
              <div className="msg-action-btn msg-action-loading">
                <Loader size={15} className="spin" />
                <span>{t.generatingPractice || 'Generating practice…'}</span>
              </div>
            ) : (
              <button onClick={onGeneratePractice} className="msg-action-btn msg-action-practice">
                <Sparkles size={15} />
                <span>{t.generatePractice || 'Generate Practice'}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ProblemSolver = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language]?.problemSolver || {};

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [solutionId, setSolutionId] = useState(null);
  const [solutionLanguage, setSolutionLanguage] = useState(null);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoPollingInterval, setVideoPollingInterval] = useState(null);

  const [similarProblems, setSimilarProblems] = useState([]);
  const [isGeneratingPractice, setIsGeneratingPractice] = useState(false);
  const [practiceGenerated, setPracticeGenerated] = useState(false);
  const [showPixelPanel, setShowPixelPanel] = useState(false);
  const [selectedPracticeProblem, setSelectedPracticeProblem] = useState(null);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [videoQuality] = useState('ql');
  const [aspectRatio] = useState('16:9');

  // Hints panel
  const [showHints, setShowHints] = useState(false);
  const [hintsTab, setHintsTab] = useState('chemistry');

  const hintsRef = useRef(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const textareaRef = useRef(null);
  const isLoadingSession = useRef(false);

  const COST_RENDER_VIDEO = 2;

  useEffect(() => {
    if (isLoadingSession.current) return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Auto-resize textarea whenever inputText changes (covers programmatic setInputText calls)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [inputText]);

  // Close hints panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (hintsRef.current && !hintsRef.current.contains(e.target)) setShowHints(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    return () => { if (videoPollingInterval) clearInterval(videoPollingInterval); };
  }, [videoPollingInterval]);


  useEffect(() => {
    if (location.state?.sessionId) {
      loadSession(location.state.sessionId);
    } else if (location.state?.question) {
      setInputText(location.state.question);
      setSelectedSubject(location.state.subject || 'general');
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = async (sid) => {
    isLoadingSession.current = true;
    try {
      const data = await getPixelSession(sid);
      setSessionId(sid);
      const loaded = data.messages.map((m, i) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        imagePreview: m.image_url || null,
        isFirst: m.role === 'assistant' && i === 1,
        solutionData: m.solution_data,
      }));
      setMessages(loaded);

      // Restore solution_id, video, and practice from the first assistant message
      const firstAI = loaded.find(m => m.role === 'assistant' && m.solutionData?.solution_id);
      if (firstAI?.solutionData) {
        const sd = firstAI.solutionData;
        if (sd.solution_id) setSolutionId(sd.solution_id);
        if (sd.video_url) {
          setVideoUrl(sd.video_url);
          setIsCreatingVideo(false);
        } else if (sd.video_status && sd.video_status !== 'solution_only' && sd.video_status !== 'failed') {
          // Video was queued/processing — resume polling
          setIsCreatingVideo(true);
          setVideoStatus(sd.video_status);
          startPolling(sd.solution_id);
        }
        if (sd.practice_problems?.length > 0) {
          setSimilarProblems(sd.practice_problems);
          setPracticeGenerated(true);
        }
      }
      // Scroll to top so user sees the first message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        isLoadingSession.current = false;
      }, 50);
    } catch (err) {
      console.error('Failed to load session:', err);
      isLoadingSession.current = false;
    }
  };

  const refreshCreditStatus = async () => {
    try {
      await getCreditStatus();
      window.dispatchEvent(new CustomEvent('creditStatusChanged'));
    } catch (err) {
      console.error('Error refreshing credits:', err);
    }
  };

  const handleSend = async (overrideLanguage = null, overrideText = null) => {
    const text = overrideText !== null ? overrideText : inputText;
    if (!text.trim() && !uploadedImageUrl) return;
    if (isLoading) return;

    const isFirst = messages.length === 0;
    const msgContent = text.trim() || '(image attached)';
    const imgPreview = uploadedImagePreview;
    const imgUrl = uploadedImageUrl;

    setMessages(prev => [...prev, { role: 'user', content: msgContent, imagePreview: imgPreview, isFirst: false }]);
    setInputText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setUploadedImageUrl(null);
    setUploadedImagePreview(null);
    setError(null);
    setIsLoading(true);

    const lang = overrideLanguage || solutionLanguage || language;

    try {
      const res = await pixelChat({ sessionId, message: msgContent, subject: selectedSubject, language: lang, imageUrl: imgUrl });

      if (isFirst) {
        setSessionId(res.session_id);
        setSolutionLanguage(lang);
        if (res.solution_data?.solution_id) setSolutionId(res.solution_data.solution_id);
        if (res.solution_data?.subject) setSelectedSubject(res.solution_data.subject);
        await refreshCreditStatus();
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.reply,
        isFirst: res.is_first_message,
        solutionData: res.solution_data || null,
        id: res.message_id || null,
      }]);
    } catch (err) {
      setError(err.message || 'Failed to get response');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };


  const handleNewChat = () => {
    if (videoPollingInterval) clearInterval(videoPollingInterval);
    setSessionId(null); setMessages([]); setInputText('');
    setUploadedImageUrl(null); setUploadedImagePreview(null); setError(null);
    setSolutionId(null); setSolutionLanguage(null); setIsCreatingVideo(false);
    setVideoStatus(null); setVideoUrl(null); setVideoPollingInterval(null);
    setSimilarProblems([]); setPracticeGenerated(false); setShowPixelPanel(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload a valid image'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB'); return; }
    try {
      setIsUploadingImage(true);
      setError(null);
      const reader = new FileReader();
      reader.onload = (ev) => setUploadedImagePreview(ev.target.result);
      reader.readAsDataURL(file);
      const publicUrl = await uploadImageBase64(file);
      setUploadedImageUrl(publicUrl);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
      setUploadedImagePreview(null);
      setUploadedImageUrl(null);
    } finally {
      setIsUploadingImage(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setUploadedImageUrl(null);
    setUploadedImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const createVideo = async () => {
    if (!solutionId) { setError('No solution available for video'); return; }
    setIsCreatingVideo(true); setVideoStatus('creating'); setError(null);
    try {
      const videoLang = solutionLanguage || language;
      const res = await renderVideoFromSolution(solutionId, videoQuality, aspectRatio, videoLang);
      // Video already rendered — show it immediately
      if (res.status === 'completed' && res.video_url) {
        setVideoUrl(res.video_url);
        setIsCreatingVideo(false);
        setVideoStatus('completed');
        return;
      }
      // In-progress or newly queued — start/resume polling
      setVideoStatus(res.status || 'queued');
      startPolling(res.video_id || res.id || solutionId);
      await refreshCreditStatus();
    } catch (err) {
      setError('Failed to create video: ' + (err.message || 'Unknown error'));
      setVideoStatus('failed');
      setIsCreatingVideo(false);
    }
  };

  const startPolling = (videoId) => {
    const interval = setInterval(async () => {
      try {
        const status = await getVideoStatus(videoId);
        setVideoStatus(status.status);
        if (status.status === 'completed' && status.video_url) {
          setVideoUrl(status.video_url);
          clearInterval(interval);
          setVideoPollingInterval(null);
          setIsCreatingVideo(false);
        } else if (status.status === 'failed') {
          setError('Video generation failed.');
          clearInterval(interval);
          setVideoPollingInterval(null);
          setIsCreatingVideo(false);
        }
      } catch (err) { console.error('Polling error:', err); }
    }, 3000);
    setVideoPollingInterval(interval);
  };

  const handleGeneratePractice = async () => {
    if (!solutionId) { setError('No solution for practice problems'); return; }
    setIsGeneratingPractice(true); setError(null);
    try {
      const res = await generateSimilarProblems(solutionId, 3, ['easy', 'medium', 'hard']);
      setSimilarProblems(res.similar_problems || res.problems || res || []);
      setPracticeGenerated(true);
    } catch (err) {
      setError(err.message || 'Failed to generate practice problems');
    } finally {
      setIsGeneratingPractice(false);
    }
  };

  const handleSubmitSolution = async (problemId, solutionData) => {
    try {
      await submitPracticeSolution({
        problem_id: problemId,
        user_solution: solutionData.userSolution,
        is_correct: solutionData.isCorrect,
        time_seconds: solutionData.timeSpent,
      });
    } catch (err) { console.error('Error submitting solution:', err); }
  };


  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const hasMessages = messages.length > 0;

  const SUGGESTIONS = [
    { icon: '📝', text: t.suggestionSolve    || 'Walk me through solving a problem step by step' },
    { icon: '💬', text: t.suggestionExplain  || 'Explain a concept I\'m struggling with' },
    { icon: '🎬', text: t.suggestionVideo    || 'Create an animated video explanation' },
    { icon: '✨', text: t.suggestionPractice || 'Give me practice problems to build confidence' },
  ];

  const SUBJECTS = [
    ['chemistry',    '⚗️', t.chemistry    || 'Chemistry'],
    ['physics',      '⚡', t.physics      || 'Physics'],
    ['mathematics',  '📐', t.mathematics  || 'Mathematics'],
  ];

  const hints = exampleTasks[language] || exampleTasks['en'];

  return (
    <div className="problem-solver-page">
      <div className="px-page">

        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageUpload} style={{ display: 'none' }} />

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="px-header">
          <div className="px-header-brand">
            <PixelIcon size="sm" />
            <span className="px-brand-name">Pixel AI</span>
            {hasMessages && selectedSubject && selectedSubject !== 'general' && (
              <span className="px-subject-badge">
                {selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)}
              </span>
            )}
          </div>
          <div className="px-header-actions">
            <button
              onClick={() => document.getElementById('history-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-icon-btn"
              title="History"
            >
              <History size={18} />
            </button>
            {hasMessages && (
              <button onClick={handleNewChat} className="px-new-btn">
                <Plus size={15} />
                {t.newChat || 'New chat'}
              </button>
            )}
          </div>
        </header>

        {/* ── Scrollable content ─────────────────────────────────────────── */}
        <div className="px-scroll">
          <div className="px-content">

            {/* Welcome screen */}
            {!hasMessages && (
              <div className="px-welcome">
                <h1 className="px-welcome-title">{t.welcomeGreeting || 'How can I help you today?'}</h1>
                <p className="px-welcome-sub">{t.welcomeReady || 'Ask me anything — type a question or snap a photo'}</p>

                <div className="px-suggestion-grid">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      className="px-suggestion-card"
                      onClick={() => { setInputText(s.text); textareaRef.current?.focus(); }}
                    >
                      <span className="px-suggestion-icon">{s.icon}</span>
                      <span className="px-suggestion-text">{s.text}</span>
                    </button>
                  ))}
                </div>

                <div className="px-subject-row">
                  {SUBJECTS.map(([subj, emoji, label]) => (
                    <button
                      key={subj}
                      className={`px-subject-btn ${selectedSubject === subj ? 'active' : ''}`}
                      onClick={() => setSelectedSubject(subj)}
                    >
                      <span>{emoji}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <ChatMessage
                key={i}
                msg={msg}
                t={t}
                onCreateVideo={createVideo}
                onGeneratePractice={handleGeneratePractice}
                onOpenPractice={() => setShowPixelPanel(true)}
                isCreatingVideo={isCreatingVideo}
                videoUrl={videoUrl}
                videoStatus={videoStatus}
                practiceReady={practiceGenerated}
                isGeneratingPractice={isGeneratingPractice}
                practiceCount={similarProblems.length}
                COST_RENDER_VIDEO={COST_RENDER_VIDEO}
              />
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="msg-ai-row">
                <PixelIcon size="sm" />
                <div className="px-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="px-error">
                <span>{error}</span>
                <button onClick={() => setError(null)}><X size={14} /></button>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>
        </div>

        {/* ── Input area ─────────────────────────────────────────────────── */}
        <div className="px-input-wrap">
          <div className="px-input-container">

            {/* ── Hints panel ──────────────────────────────────────────── */}
            {showHints && (
              <div className="px-hints-card" ref={hintsRef}>
                <div className="pxh-header">
                  <div>
                    <span className="pxh-title"><Lightbulb size={14} /> {hints?.sectionTitle || 'Example questions'}</span>
                    <span className="pxh-sub">Tap any to try it</span>
                  </div>
                  <button className="pxs-close" onClick={() => setShowHints(false)}><X size={14} /></button>
                </div>
                <div className="pxh-tabs">
                  {['chemistry', 'physics', 'mathematics'].map(subj => (
                    <button
                      key={subj}
                      className={`pxh-tab ${hintsTab === subj ? 'active' : ''}`}
                      onClick={() => setHintsTab(subj)}
                    >{hints?.[subj]?.title || subj}</button>
                  ))}
                </div>
                <div className="pxh-list">
                  {hints?.[hintsTab]?.task && (
                    <button
                      className="pxh-item"
                      onClick={() => {
                        setInputText(hints[hintsTab].task);
                        setSelectedSubject(hintsTab);
                        setShowHints(false);
                        textareaRef.current?.focus();
                      }}
                    >
                      <span className="pxh-item-text">{hints[hintsTab].task}</span>
                      <ChevronRight size={14} className="pxh-arrow" />
                    </button>
                  )}
                </div>
                {hints?.disclaimer && <p className="pxh-disclaimer">{hints.disclaimer}</p>}
              </div>
            )}

            {uploadedImagePreview && (
              <div className="px-image-preview">
                <img src={uploadedImagePreview} alt="attach" />
                <button onClick={handleRemoveImage}><X size={12} /></button>
              </div>
            )}

            <div className="px-input-box">
              <button onClick={() => fileInputRef.current?.click()} className="px-attach-btn" disabled={isUploadingImage} title="Upload image">
                {isUploadingImage ? <Loader size={18} className="spin" /> : <Paperclip size={18} />}
              </button>
              <button onClick={() => cameraInputRef.current?.click()} className="px-attach-btn" disabled={isUploadingImage} title="Take photo">
                <Camera size={18} />
              </button>
              <button
                onClick={() => setShowHints(s => !s)}
                className={`px-attach-btn ${showHints ? 'active' : ''}`}
                title="Examples"
              >
                <Lightbulb size={18} />
              </button>
              <textarea
                ref={textareaRef}
                className="px-textarea"
                placeholder={hasMessages ? (t.followUpPlaceholder || 'Ask a follow-up question…') : (t.questionPlaceholder || 'Ask anything in chemistry, physics or mathematics…')}
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value);
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                onClick={() => handleSend()}
                disabled={(!inputText.trim() && !uploadedImageUrl) || isLoading || isUploadingImage}
                className="px-send-btn"
              >
                <ArrowUp size={18} />
              </button>
            </div>
            <p className="px-disclaimer">Pixel AI can make mistakes. Verify important information.</p>
          </div>
        </div>

      </div>

      {/* ── Practice list modal ─────────────────────────────────────────────── */}
      {showPixelPanel && similarProblems.length > 0 && (
        <div
          onClick={() => setShowPixelPanel(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1500,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 20, width: '100%', maxWidth: 580,
              maxHeight: '85vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
            }}
          >
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>
                📚 {t.practiceProblems || 'Practice Problems'} ({similarProblems.length})
              </span>
              <button onClick={() => setShowPixelPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0.9rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {similarProblems.map((problem, index) => {
                const diff = (problem.difficulty || 'medium').toLowerCase();
                const diffColor = { easy: '#15803d', medium: '#92400e', hard: '#991b1b' }[diff] || '#92400e';
                const diffBg   = { easy: '#dcfce7', medium: '#fef3c7', hard: '#fee2e2' }[diff] || '#fef3c7';
                return (
                  <button
                    key={index}
                    onClick={() => { setSelectedPracticeProblem(problem); setCurrentProblemIndex(index); setIsModalOpen(true); setShowPixelPanel(false); }}
                    style={{
                      textAlign: 'left', background: '#f8f9ff',
                      border: '1.5px solid #e0d6f5', borderRadius: 12,
                      padding: '0.8rem 1rem', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: '#5B35A0' }}>#{index + 1}</span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: diffBg, color: diffColor }}>
                        {problem.difficulty}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#333', lineHeight: 1.5 }}>
                      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {((problem.problem_text || '').replace(/\\n/g, '\n')).slice(0, 200) + ((problem.problem_text || '').length > 200 ? '…' : '')}
                      </ReactMarkdown>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && selectedPracticeProblem && (
        <PracticeProblemModal
          problem={selectedPracticeProblem}
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedPracticeProblem(null); }}
          onSubmit={handleSubmitSolution}
          hasNext={currentProblemIndex < similarProblems.length - 1}
          hasPrevious={currentProblemIndex > 0}
          onNext={() => { const n = currentProblemIndex + 1; setCurrentProblemIndex(n); setSelectedPracticeProblem(similarProblems[n]); }}
          onPrevious={() => { const n = currentProblemIndex - 1; setCurrentProblemIndex(n); setSelectedPracticeProblem(similarProblems[n]); }}
          currentIndex={currentProblemIndex}
          totalProblems={similarProblems.length}
        />
      )}
    </div>
  );
};

export default ProblemSolver;
